import React, { useState, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { range } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import {
  setSelectedYear,
  updateScenario,
} from 'spw-redux/modules/calculators/actions';
import Growths from 'spw-components/growth-calculator/growths';
import { Row, SPWClickAwayTooltip } from 'spw-shared/globalComponents';

import { selectIsSavingScenario } from 'spw-redux/modules/calculators/selectors';
import {
  CalculatorType,
  YEARS_AHEAD,
  BUILDING_HEADCOUNT_SEATCOUNT,
  FLOOR_HEADCOUNT_SEATCOUNT,
} from 'spw-redux/modules/calculators/types';
import { GROWTH_CALCULATORS } from 'spw-constants';
import { handleOnKeyPress, isDepartmentsTypeScenario } from 'spw-shared/utils';
import { isValidCurrentYear } from 'spw-shared/validations';
import { IProject } from 'spw-redux/modules/projects/types';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import {
  CalculatorTable,
  HeadcountInput,
  HeaderCell,
  BodyCell,
  CalculatorValue,
  TitleHeaderCell,
  GrowthRateHeaderCell,
} from './styles';
import { isScenarioLinked } from './total-program-utils';
import { PlainProgramScenario } from '../../models/Scenario';
import GrowthRateGuide from './growth-rate-guide';

const { buildingHeadcountSeatcountDepartments } = GROWTH_CALCULATORS;

interface GrowthTableProps {
  project: IProject;
  scenario: PlainProgramScenario | null;
  calculatorType: CalculatorType;
  isEditingSavedScenario: boolean;
  totalPrograms?: PlainProgramScenario[];
  testingIdPrefix?: string;
  isRenderingForExport?: boolean;
}

const GrowthTable: React.FC<GrowthTableProps> = ({
  scenario,
  calculatorType,
  isEditingSavedScenario,
  totalPrograms,
  testingIdPrefix,
  isRenderingForExport,
}) => {
  const [editing, setEditing] = useState('');
  const [editValue, setEditValue] = useState('');
  const [currentYearInputTouched, setCurrentYearInputTouched] = useState(false);
  const dispatch = window.SPWExport ? Function.prototype : useDispatch();
  const isSavingScenario = useSelector(selectIsSavingScenario);

  const shouldHighlightCurrentYearInput = useMemo(() => {
    if (
      !scenario.id &&
      !isEditingSavedScenario &&
      !isSavingScenario &&
      [BUILDING_HEADCOUNT_SEATCOUNT, FLOOR_HEADCOUNT_SEATCOUNT].includes(
        calculatorType
      )
    ) {
      return !currentYearInputTouched;
    }

    return false;
  }, [
    calculatorType,
    currentYearInputTouched,
    scenario.id,
    isEditingSavedScenario,
    isSavingScenario,
  ]);

  const { startYear } = scenario;
  const selectedIndex = scenario.growths?.[0]?.growthEntries.findIndex(
    (ge) => ge.year === scenario.targetYear
  );

  const handleInitialYearClicked = useCallback(() => {
    setEditing('initial-year');
    if (startYear === 0) {
      setEditValue('');
    } else {
      setEditValue(startYear.toString());
    }
  }, [startYear, setEditing, setEditValue]);

  if (!scenario) return null;

  const disabledInputs =
    window.SPWExport || isRenderingForExport
      ? false
      : scenario?.id && !isEditingSavedScenario;

  let isLinked = false;
  if (isDepartmentsTypeScenario(scenario) && totalPrograms?.length) {
    isLinked = isScenarioLinked(scenario.id, totalPrograms);
  }

  // alison  5:35 PM
  // Vacancy Factor %: 0.0 min and 99.0 max
  // Headcount Utilization %: 1 min and 100 max
  // Desk Sharing Ratio: 1: 0.10 min and 1: 9.99 max
  // Growth Rate %: -10.0 min and 10.0 max

  return (
    <TableContainer>
      <CalculatorTable>
        <TableHead>
          <TableRow>
            <TitleHeaderCell>ENTER HEADCOUNT</TitleHeaderCell>
            <GrowthRateHeaderCell align="center">
              <Row>
                <span>GROWTH %</span>
                <SPWClickAwayTooltip
                  width={298}
                  height={439}
                  placement="bottom-right"
                >
                  <GrowthRateGuide />
                </SPWClickAwayTooltip>
              </Row>
            </GrowthRateHeaderCell>
            <HeaderCell
              className={
                startYear === scenario?.targetYear
                  ? 'calculator-selected-year'
                  : ''
              }
              align="center"
              onClick={() =>
                !disabledInputs &&
                !isLinked &&
                dispatch(setSelectedYear(startYear))
              }
            >
              CURRENT
            </HeaderCell>
            {range(1, YEARS_AHEAD + 1).map((year) => (
              <HeaderCell
                onClick={() => {
                  if (year <= scenario?.growthYears) {
                    if (!disabledInputs && !isLinked) {
                      dispatch(setSelectedYear(startYear + year));
                    }
                  }
                }}
                key={`year-${year}`}
                className={`
                  ${
                    year === selectedIndex + 1 ? 'calculator-selected-year' : ''
                  }
                `}
                align="center"
              >
                YEAR {year}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            <BodyCell colSpan={2} align="left" />
            <BodyCell
              className={
                startYear === scenario?.targetYear
                  ? 'calculator-selected-year'
                  : ''
              }
              align="center"
              variant="body"
              onClick={() =>
                !disabledInputs &&
                !isLinked &&
                dispatch(setSelectedYear(startYear))
              }
              data-testid={`${testingIdPrefix}_year_0`}
            >
              {calculatorType === buildingHeadcountSeatcountDepartments ||
              isTotalProgram(scenario) ? (
                startYear
              ) : (
                <HeadcountInput
                  onClick={(event) => event.stopPropagation()}
                  disabled={disabledInputs || isLinked}
                  type="number"
                  value={editing === 'initial-year' ? editValue : startYear}
                  onFocus={handleInitialYearClicked}
                  onKeyPress={handleOnKeyPress}
                  onBlur={() => {
                    setCurrentYearInputTouched(true);
                    if (!disabledInputs) {
                      let scenarioStartYear = parseInt(editValue, 10);

                      if (
                        _.isNaN(scenarioStartYear) ||
                        !isValidCurrentYear(scenarioStartYear.toString())
                      ) {
                        // these seem like reasonable years to allow
                        scenarioStartYear = startYear;
                      }
                      const yearsDifference =
                        scenario.targetYear - scenario.startYear;

                      dispatch(
                        updateScenario(scenario, {
                          startYear: scenarioStartYear,
                        })
                      );
                      dispatch(
                        setSelectedYear(scenarioStartYear + yearsDifference)
                      );
                    }
                    setEditValue('');
                    setEditing('');
                  }}
                  onChange={(e) =>
                    setEditValue(e.target.value.replace(/[^0-9]{4}/g, ''))
                  }
                  className={shouldHighlightCurrentYearInput ? 'highlight' : ''}
                />
              )}
            </BodyCell>

            {range(startYear + 1, startYear + scenario?.growthYears + 1).map(
              (year: number, index: number) => (
                <BodyCell
                  onClick={() =>
                    !disabledInputs &&
                    !isLinked &&
                    dispatch(setSelectedYear(year))
                  }
                  className={[
                    'calculator-value',
                    index === selectedIndex ? 'calculator-selected-year' : '',
                  ].join(' ')}
                  key={`readYear-${year}`}
                  data-testid={`${testingIdPrefix}_year_${index + 1}`}
                >
                  <CalculatorValue>{year}</CalculatorValue>
                </BodyCell>
              )
            )}
            {range(scenario?.growthYears + 1, YEARS_AHEAD + 1).map(
              (year: number) => (
                <BodyCell
                  className="calculator-value"
                  key={`readYear-${year}`}
                />
              )
            )}
          </TableRow>

          <Growths
            testingIdPrefix={testingIdPrefix}
            isRenderingForExport={isRenderingForExport}
            isLinked={isLinked}
          />
        </TableBody>
      </CalculatorTable>
    </TableContainer>
  );
};

export default GrowthTable;
